import { useAtom } from "jotai";
import { backendDataAtom } from "../atoms/backendDataAtom.js";
import {
  backendGet,
  backendPatch,
  backendPost,
} from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";

export function useBlogPostActions() {
  const [backendData, setBackendData] = useAtom(backendDataAtom);
  const { enqueueSnackbar } = useSnackbar();

  const state = backendData?.data
    ? backendData
    : {
        data: {},
        relations: {},
        meta: {},
      };

  const setState = (data) => {
    setBackendData((prev) => ({
      ...prev,
      data: data,
    }));
  };

  /**
   * BlogPost nach ID suchen und im globalen Zustand speichern
   *
   * @param id
   * @param props
   * @returns {Promise<*|null>}
   */
  const findById = (id, props) => {
    return backendGet(`data/blog-posts/${id}`, {
      ...props,
      setResult: (res) => {
        setBackendData({
          data: res,
          relations: {},
          id: id,
          type: "blogPost",
        });
      },
      enqueueSnackbar,
    });
  };

  /**
   * BlogPost Status aktualisieren
   *
   * @param blogPostId
   * @param status
   * @param options
   * @returns {Promise<void>}
   */
  const updateBlogPostStatus = async (blogPostId, status, options) => {
    const {
      label = "News-Beitrag Status aktualisieren",
      setIsLoading = () => {},
    } = options || {};

    return await backendPatch(`data/blog-posts/${blogPostId}/status`, {
      label,
      toSend: { status },
      setIsLoading,
      enqueueSnackbar,
      setResult: (res) => {
        const data = {
          ...backendData.data,
          ...res,
        };

        setState(data);
      },
    });
  };

  /**
   * BlogPost aktualisieren
   *
   * @param blogPostId
   * @param props
   * @returns {Promise<void>}
   */
  const updateBlogPost = async (blogPostId, props) => {
    const {
      label = "News-Beitrag aktualisieren",
      setIsLoading = () => {},
      route = `data/blog-posts/${blogPostId}`,
    } = props || {};

    return await backendPatch(route, {
      ...props,
      label,
      setIsLoading,
      enqueueSnackbar,
      setResult: (res) => {
        const data = {
          ...backendData.data,
          ...res,
        };

        setState(data);
      },
    });
  };

  /**
   * BlogPost Roles aktualisieren
   *
   * @param blogPostId
   * @param props
   * @returns {Promise<void>}
   */
  const updateBlogPostRoles = async (blogPostId, props) => {
    const { label = "News-Beitrag Rollenberechtigung aktualisieren" } =
      props || {};

    return updateBlogPost(blogPostId, {
      ...props,
      label,
      route: `data/blog-posts/${blogPostId}/roles`,
    });
  };

  /**
   * BlogPost Customers aktualisieren
   *
   * @param blogPostId
   * @param props
   * @returns {Promise<void>}
   */
  const updateBlogPostCustomers = async (blogPostId, props) => {
    const { label = "News-Beitrag Kundenberechtigung aktualisieren" } =
      props || {};

    return updateBlogPost(blogPostId, {
      ...props,
      label,
      route: `data/blog-posts/${blogPostId}/customers`,
    });
  };

  /**
   * BlogPost Authors aktualisieren
   *
   * @param blogPostId
   * @param props
   * @returns {Promise<void>}
   */
  const updateBlogPostAuthors = async (blogPostId, props) => {
    const { label = "News-Beitrag Autoren aktualisieren" } = props || {};

    return updateBlogPost(blogPostId, {
      ...props,
      label,
      route: `data/blog-posts/${blogPostId}/authors`,
    });
  };

  const lastestPublishedBlogPost = async (props) => {
    return backendGet(`data/blog-posts/latest`, {
      ...props,
      options: {
        params: {
          include: ["authors", "tags"],
          filter: { _latest: true },
        },
      },
      enqueueSnackbar,
    });
  };

  const markAsRead = async (blogPostId, props) => {
    const {
      label = "Als gelesen markieren",
      setIsLoading = () => {},
      route = `data/blog-posts/${blogPostId}/read`,
    } = props || {};

    return await backendPatch(route, {
      ...props,
      label,
      setIsLoading,
      enqueueSnackbar,
    });
  };

  const updateBlogPostTags = async (blogPostId, props) => {
    const { label = "News-Beitrag Tags aktualisieren" } = props || {};

    return updateBlogPost(blogPostId, {
      ...props,
      label,
      route: `data/blog-posts/${blogPostId}/tags`,
    });
  };

  const createBlogPostTag = async (tagName, props) => {
    const { label = "News-Beitrag Tag erstellen", setIsLoading = () => {} } =
      props || {};

    return await backendPost(`data/blog-tags`, {
      label,
      toSend: { name: tagName },
      setIsLoading,
      enqueueSnackbar,
    });
  };

  return {
    createBlogPostTag,
    updateBlogPostStatus,
    updateBlogPostCustomers,
    updateBlogPostRoles,
    updateBlogPostAuthors,
    updateBlogPostTags,
    setState,
    state,
    findById,
    lastestPublishedBlogPost,
    markAsRead,
  };
}
